import { useMemo } from 'react';

import { bindActionCreators, useDispatch } from 'third-party';

import {
  organizationSingleEndpoint,
  userAuthEndpoint,
  userAvatarEndpoint,
  userEndpoint,
  userInfoEndpoint,
  userPasswordResetLinkEndpoint,
  userRefreshTokenEndpoint,
  userResetPasswordEndpoint,
  validateUserResetTokenPasswordEndpoint,
} from 'constants/endpoints';

import { saveUserToken } from 'utils/auth';
import { httpMethod, simplifyBuilder } from 'utils/sra';
import { getFromStorage, saveInStorage } from 'utils/storage';

import {
  APIOrganization,
  APIUser,
  APIUserOrganization,
  APIUserRegister,
  APIUserResetPasswordModel,
  APIUserType,
} from 'types/api';

export type AppState = {
  user: APIUser | null;
  userInfoPending: boolean;
  isAppLoaded: boolean;
  userLoginInPending: boolean;
  userRegisterPending: boolean;
  updateAvatarPending: boolean;
  updateSelfInfoPending: boolean;
  isNavigationCollapsed: boolean;
  deleteOrganizationPending: boolean;
};

export const appState: AppState = {
  user: null,
  userInfoPending: false,
  isAppLoaded: false,
  updateAvatarPending: false,
  updateSelfInfoPending: false,
  isNavigationCollapsed: getFromStorage('isNavigationCollapsed', false),
  userLoginInPending: false,
  userRegisterPending: false,
  deleteOrganizationPending: false,
};

const builder = simplifyBuilder(appState, {});

export const setAppIsLoaded = builder.createReduxAction(
  (isAppLoaded: boolean) => ({
    name: 'setUser',
    updater: () => ({
      isAppLoaded: isAppLoaded,
    }),
  }),
);

export const setUser = builder.createReduxAction(
  (user: APIUser | undefined) => ({
    name: 'setUser',
    updater: () => ({
      user: user,
    }),
  }),
);

const toggleNavigation = builder.createReduxAction(() => ({
  name: 'setUser',
  updater: state => {
    saveInStorage('isNavigationCollapsed', !state.isNavigationCollapsed);
    return {
      isNavigationCollapsed: !state.isNavigationCollapsed,
    };
  },
}));

export const userLogOut = builder.createReduxAction(() => ({
  name: 'userLogOut',
  updater: () => ({
    user: null,
  }),
}));

const userLogIn = builder.createServerAction(
  (email: string, password: string) => ({
    name: 'userLoginIn',
    url: userAuthEndpoint(),
    method: httpMethod.post,
    body: { email, password },
    onRequest: () => ({
      userLoginInErrorText: '',
    }),
    onSuccess: (state: AppState, payload: string) => {
      saveUserToken(payload);
    },
  }),
);

const userSendPasswordResetLink = builder.createServerAction(
  (email: string) => ({
    name: 'userSendPasswordResetLink',
    url: userPasswordResetLinkEndpoint(email),
    method: httpMethod.put,
  }),
);

const userResetPassword = builder.createServerAction(
  (model: APIUserResetPasswordModel) => ({
    name: 'userResetPassword',
    url: userResetPasswordEndpoint(),
    method: httpMethod.put,
    body: model,
    onRequest: () => ({
      userResetErrorText: '',
    }),
  }),
);

const validateUserResetPasswordToken = builder.createServerAction(
  (email: string, token: string) => ({
    name: 'validateUserResetPasswordToken',
    url: validateUserResetTokenPasswordEndpoint(email, token),
    method: httpMethod.get,
    // onSuccess: we don't put anything to store, just return result for typeahead
  }),
);

const userInfo = builder.createServerAction(() => ({
  name: 'userInfo',
  url: userInfoEndpoint(),
  method: httpMethod.get,
  onSuccess: (state: AppState, payload: APIUser) => {
    // refresh token if available
    if (payload.token) {
      saveUserToken(payload.token);
    }

    return {
      user: {
        ...payload,
        organizations: payload.userOrganizations.map(it => ({
          ...it,
          iseSelected: false,
        })),
      },
    };
  },
}));

const refreshTokens = builder.createServerAction(() => ({
  name: 'refreshTokens',
  url: userRefreshTokenEndpoint(),
  method: httpMethod.post,
  onSuccess: (state: AppState, payload: string) => {
    saveUserToken(payload);
  },
}));

const userRegister = builder.createServerAction((model: APIUserRegister) => ({
  name: 'userRegister',
  url: userEndpoint(),
  method: httpMethod.put,
  body: model,
  onRequest: () => ({
    userLoginInErrorText: '',
  }),
  onSuccess: (state: AppState, payload: string) => {
    saveUserToken(payload);
  },
}));

const buildExternalMembership = (
  organization: APIOrganization,
): APIUserOrganization => ({
  organization,
  userType: APIUserType.SuperAdmin,
  userRights: [],
  isSelected: true,
});

const selectWithinMemberships = (
  state: AppState,
  organizationId: string | undefined,
  externalOrganization?: APIOrganization,
): Partial<AppState> => {
  if (!state.user) {
    return {};
  }

  // Drop any previously injected external organization before re-selecting.
  const memberships = (state.user.userOrganizations ?? []).filter(
    it => it.userType !== APIUserType.SuperAdmin,
  );

  const isMember = memberships.some(
    it => it.organization.id === organizationId,
  );

  const userOrganizations = memberships.map(it => ({
    ...it,
    isSelected: it.organization.id === organizationId,
  }));

  if (
    !isMember &&
    externalOrganization &&
    state.user.userType === APIUserType.SuperAdmin
  ) {
    userOrganizations.push(buildExternalMembership(externalOrganization));
  }

  return { user: { ...state.user, userOrganizations } };
};

export const selectOrganization = builder.createReduxAction(
  (organizationId: string | undefined, organization?: APIOrganization) => ({
    name: 'selectOrganization',
    updater: (state: AppState) =>
      selectWithinMemberships(state, organizationId, organization),
  }),
);

const viewExternalOrganization = builder.createServerAction(
  (organizationId: string) => ({
    name: 'viewExternalOrganization',
    url: organizationSingleEndpoint(organizationId),
    method: httpMethod.get,
    onSuccess: (state: AppState, payload: APIOrganization) =>
      selectWithinMemberships(state, organizationId, payload),
  }),
);

const updateAvatar = builder.createServerAction((file: any) => ({
  name: 'updateAvatar',
  url: userAvatarEndpoint(),
  method: httpMethod.put,
  body: file,
  onSuccess: (state: AppState, payload: APIUser) => {
    return {
      user: {
        ...state.user,
        avatar: payload.avatar,
      },
    };
  },
}));

const updateSelfInfo = builder.createServerAction((userModel: APIUser) => ({
  name: 'updateSelfInfo',
  url: userInfoEndpoint(),
  method: httpMethod.put,
  body: userModel,
  onSuccess: (state: AppState, payload: APIUser) => {
    return {
      user: {
        ...state.user,
        firstName: payload.firstName,
        lastName: payload.lastName,
        phone: payload.phone,
      },
    };
  },
}));

export const useApp = () => {
  const dispatch = useDispatch();

  return useMemo(
    () =>
      bindActionCreators(
        {
          refreshTokens,
          selectOrganization,
          viewExternalOrganization,
          userRegister,
          userSendPasswordResetLink,
          validateUserResetPasswordToken,
          userResetPassword,
          setUser,
          updateAvatar,
          updateSelfInfo,
          userInfo,
          toggleNavigation,
          setAppIsLoaded,
          userLogOut,
          userLogIn,
        },
        dispatch,
      ),
    [dispatch],
  );
};

export const appReducer = builder.getReducers();
